import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PaginationListType } from '../Types/PaginationParams';

interface UseSearchFilterProps {
    initialQuery?: string | null;
    onQueryChange?: (query: string | null) => void;
    totalItems?: number;
    itemsPerPage?: number;
    listType?: PaginationListType;
}

const getQueryParam = (listType: PaginationListType): string => {
    return `query[${listType.toLowerCase()}]`;
};

const useSearchFilter = ({
    initialQuery = null,
    onQueryChange,
    totalItems,
    itemsPerPage,
    listType,
}: UseSearchFilterProps = {}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParam = listType ? getQueryParam(listType) : null;
    const queryFromUrl = queryParam ? searchParams.get(queryParam) : null;
    const [query, setQuery] = useState(queryFromUrl || initialQuery);
    const [localQuery, setLocalQuery] = useState(queryFromUrl || initialQuery);
    const [hasBeenVisible, setHasBeenVisible] = useState(false);
    const changeSourceRef = useRef<'user' | 'url' | null>(null);
    const previousQueryRef = useRef<string | null>(queryFromUrl || initialQuery);

    const showInput = useMemo(() => {
        if (itemsPerPage && totalItems !== undefined) {
            const shouldShow = totalItems > itemsPerPage || hasBeenVisible || (query !== null && query !== '');
            if (shouldShow && !hasBeenVisible) {
                setHasBeenVisible(true);
            }
            return shouldShow;
        }
        return true;
    }, [itemsPerPage, totalItems, hasBeenVisible, query]);

    const applyQuery = useCallback(() => {
        const newQuery = localQuery || initialQuery;
        const hasQueryChanged = previousQueryRef.current !== newQuery;
        setQuery(newQuery);
        previousQueryRef.current = newQuery;

        if (queryParam) {
            changeSourceRef.current = 'user';
            const params = new URLSearchParams(searchParams.toString());
            if (newQuery && newQuery.trim() !== '') {
                params.set(queryParam, newQuery);
            } else {
                params.delete(queryParam);
            }

            if (listType && hasQueryChanged) {
                const pageParam = `page[${listType.toLowerCase()}]`;
                params.delete(pageParam);
            }

            setSearchParams(params, { replace: false });
        }

        if (onQueryChange) {
            onQueryChange(newQuery);
        }
    }, [localQuery, onQueryChange, initialQuery, queryParam, searchParams, setSearchParams, listType]);

    useEffect(() => {
        if (queryParam) {
            const queryFromUrl = searchParams.get(queryParam);
            if (queryFromUrl !== query && changeSourceRef.current !== 'user') {
                changeSourceRef.current = 'url';
                setQuery(queryFromUrl);
                setLocalQuery(queryFromUrl);
                previousQueryRef.current = queryFromUrl;
                if (onQueryChange) {
                    onQueryChange(queryFromUrl);
                }
                setTimeout(() => {
                    changeSourceRef.current = null;
                }, 0);
            }
        }
    }, [searchParams, queryParam, query, onQueryChange]);

    useEffect(() => {
        if (localQuery === '') {
            applyQuery();
        }
    }, [localQuery, applyQuery]);

    useEffect(() => {
        if (changeSourceRef.current === 'user') {
            setTimeout(() => {
                changeSourceRef.current = null;
            }, 0);
        }
    }, [query]);

    const setLocalQueryValue = useCallback((value: string) => {
        setLocalQuery(value);
    }, []);

    const clearQuery = useCallback(() => {
        setLocalQuery('');
        setQuery('');
        previousQueryRef.current = '';

        if (queryParam) {
            changeSourceRef.current = 'user';
            const params = new URLSearchParams(searchParams.toString());
            params.delete(queryParam);

            if (listType) {
                const pageParam = `page[${listType.toLowerCase()}]`;
                params.delete(pageParam);
            }

            setSearchParams(params, { replace: false });
        }

        if (onQueryChange) {
            onQueryChange('');
        }
    }, [onQueryChange, queryParam, searchParams, setSearchParams, listType]);

    const searchActions = useMemo(
        () => ({
            setLocalQuery: setLocalQueryValue,
            applyQuery,
            clearQuery,
        }),
        [setLocalQueryValue, applyQuery, clearQuery],
    );

    return {
        query,
        localQuery,
        searchActions,
        setQuery,
        setLocalQuery: setLocalQueryValue,
        applyQuery,
        clearQuery,
        showInput,
    };
};

export default useSearchFilter;
